import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Record } from 'immutable';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useMeetingItemQueryParam } from 'Shared/components/MeetingItemDetailPanel/useMeetingItemQueryParam';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

// Types
import { MeetingItem } from 'Shared/types/MeetingItem.type';

// Actions
import * as workspaceItemCommentActions from 'Shared/actions/WorkspaceItemComments.actions';
import { setViewedWorkspaceItemComments } from 'Shared/actions/todos';

// Helpers
import MeetingItemHelpers from 'Shared/model_helpers/MeetingItem.helper';
import WorkspaceItemCommentHelper from 'Shared/model_helpers/WorkspaceItemComment.helper';

// Selectors
import * as workspaceItemCommentsSelectors from 'Shared/selectors/WorkspaceItemComments.selectors';

// Components
import { WpButton } from 'Wpds/components/button';
import CommentsListItem from 'Shared/components/Comments/CommentListItem';
import MeetingItemCommentsLoading from './MeetingItemCommentsLoading';
import { WpBadge } from 'Wpds/components/badge';

// Styles
import './MeetingItemComments.scss';

export enum SortDirection {
  OLDEST_FIRST,
  NEWEST_FIRST,
}

type Props = {
  meetingItem: Record<MeetingItem>;
  className?: string;
  emptyStateComponent?: React.ReactNode;
  truncateLimit?: number;
  // Used in meeting mode to trigger the typing participants
  // warning
  setIsEditing?: Function;
  sort?: SortDirection;
};

const MeetingItemComments = ({
  meetingItem,
  className,
  emptyStateComponent,
  truncateLimit,
  setIsEditing,
  sort = SortDirection.OLDEST_FIRST,
}: Props) => {
  const dispatch = useDispatch();
  const [isFetching, toggleIsFetching] = useToggle(false);
  // const [viewFullList, toggleViewFullList] = useToggle(false);
  const [viewFullList, setViewFullList] = useState(false);
  const { tr } = useTranslation();
  const scrollToRef = useRef(null);
  const meetingItemId = MeetingItemHelpers.getId(meetingItem);
  const commentsCount = MeetingItemHelpers.getCommentsCount(meetingItem);
  const meetingItemUuid = MeetingItemHelpers.getUuid(meetingItem);
  const workspaceId = MeetingItemHelpers.getWorkspaceId(meetingItem);

  const shouldShowAllComments = useMeetingItemQueryParam({ paramKey: 'showAllComments', defaultValue: false });

  // const EmptyState = emptyStateComponent;

  useEffect(() => {
    setViewFullList(shouldShowAllComments);
  }, [shouldShowAllComments]);

  const comments = useSelector(state =>
    workspaceItemCommentsSelectors.getCommentsByWorkspaceItemId(state, { meetingItemId })
  );

  useEffect(() => {
    // Remove New Comment badge if it is in workspace.notifications
    dispatch(setViewedWorkspaceItemComments(meetingItemUuid));
  }, [comments]);

  useEffect(() => {
    toggleIsFetching();
    dispatch(workspaceItemCommentActions.fetchWorkspaceItemComments(meetingItemId)).finally(() => {
      toggleIsFetching();
    });
  }, [dispatch, meetingItemId, toggleIsFetching, commentsCount]);

  const handleDeleteCommentClick = useCallback(
    workspaceItemCommentUuid => {
      dispatch(workspaceItemCommentActions.deleteWorkspaceItemComment(workspaceItemCommentUuid)).then(response => {
        return response;
      });
    },
    [dispatch, workspaceItemCommentActions]
  );

  const handleUpdateCommentClick = useCallback(
    data => {
      return dispatch(workspaceItemCommentActions.updateWorkspaceItemComment(data));
    },
    [dispatch, workspaceItemCommentActions]
  );

  const showLoadingState = isFetching && comments.size === 0;
  const showEmptyState = comments.size === 0 && !isFetching;

  const truncateListTosize = truncateLimit || 4;
  const remainingSize = viewFullList ? 0 : comments.size - truncateListTosize;
  const hasMoreComments = remainingSize > 0;

  const sortedComments = sort === SortDirection.NEWEST_FIRST ? comments.reverse() : comments;

  const listToRender =
    viewFullList || !hasMoreComments
      ? sortedComments
      : sort === SortDirection.NEWEST_FIRST
      ? sortedComments.slice(0, truncateListTosize)
      : sortedComments.slice(sortedComments.size - truncateListTosize, sortedComments.size);

  const baseClassName = 'MeetingItemComments';
  return (
    <div className={`${baseClassName} ${className}`} ref={scrollToRef}>
      <div className={`${baseClassName}__comments ${className ? `${className}__comments` : ``}`} ref={scrollToRef}>
        {hasMoreComments && sort === SortDirection.OLDEST_FIRST && (
          <div
            className={`${baseClassName}__comments__see-more ${baseClassName}__comments__see-more--top`}
            onClick={() => setViewFullList(true)}
          >
            <WpButton variant="tertiary" size="sm" onClick={() => setViewFullList(true)}>
              {remainingSize > 1
                ? tr('perform.meeting_item.comments.see_more_plural', `${remainingSize}`)
                : tr('perform.meeting_item.comments.see_more_singular', `${remainingSize}`)}
            </WpButton>
          </div>
        )}
        <TransitionGroup>
          {listToRender.valueSeq().map(comment => (
            <CSSTransition
              key={WorkspaceItemCommentHelper.getUuid(comment)}
              timeout={500}
              classNames={`${baseClassName}__animation`}
            >
              <CommentsListItem
                key={WorkspaceItemCommentHelper.getUuid(comment)}
                comment={comment.toJS()}
                workspaceId={workspaceId}
                handleDeleteComment={handleDeleteCommentClick}
                handleUpdateComment={handleUpdateCommentClick}
                setIsEditing={setIsEditing}
                badge={
                  WorkspaceItemCommentHelper.getMeetingModeSessionId(comment) && (
                    <WpBadge color="blue" size="sm">
                      {tr('perform.comments.badge.meeting_notes')}
                    </WpBadge>
                  )
                }
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
        {hasMoreComments && sort === SortDirection.NEWEST_FIRST && (
          <div
            className={`${baseClassName}__comments__see-more ${baseClassName}__comments__see-more--bottom`}
            onClick={() => setViewFullList(true)}
          >
            <WpButton variant="tertiary" size="sm" onClick={() => setViewFullList(true)}>
              {remainingSize > 1
                ? tr('perform.meeting_item.comments.see_more_plural', `${remainingSize}`)
                : tr('perform.meeting_item.comments.see_more_singular', `${remainingSize}`)}
            </WpButton>
          </div>
        )}
        {showEmptyState && emptyStateComponent && <>{emptyStateComponent}</>}
        {showEmptyState && !emptyStateComponent && (
          <div className={`${baseClassName}__comments__empty`}>
            <p>{tr('perform.meeting_item.comments.empty')}</p>
          </div>
        )}
        {showLoadingState && <MeetingItemCommentsLoading countToRender={listToRender.size > 0 ? 1 : 2} />}
      </div>
    </div>
  );
};

export default MeetingItemComments;
